import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { quotePdfBase64 } from "@/lib/quotePdf";
import { toast } from "sonner";

export default function EmailQuoteDialog({ open, onOpenChange, quote, logoDataUrl, onSent }) {
  const [to, setTo] = useState(quote?.customer?.email || "");
  const [message, setMessage] = useState(quote?.generated?.customer_message || "");
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    if (open) {
      setTo(quote?.customer?.email || "");
      setMessage(quote?.generated?.customer_message || "");
    }
  }, [open, quote]);

  const send = async () => {
    if (!to || !to.includes("@")) {
      toast.error("Enter a valid customer email.");
      return;
    }
    setSending(true);
    try {
      const pdf_base64 = quotePdfBase64(quote, logoDataUrl);
      const r = await api.post(`/quotes/${quote.id}/email`, { to, message, pdf_base64 });
      if (r.data?.mocked) {
        toast.success(`Email sent (MOCKED — set RESEND_API_KEY to deliver for real)`);
      } else {
        toast.success(`Quote emailed to ${to}`);
      }
      onSent && onSent();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Email this quote</DialogTitle>
          <DialogDescription>
            We'll attach the PDF and send your customer message below.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Customer email</Label>
            <Input
              type="email" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="customer@example.com"
              data-testid="email-to-input"
            />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea
              value={message} onChange={(e) => setMessage(e.target.value)}
              className="min-h-[120px]"
              placeholder="Hi [Customer], attached is the quote for the work discussed…"
              data-testid="email-message-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button
            onClick={send} disabled={sending}
            data-testid="email-send-button"
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><Mail className="h-4 w-4 mr-2" /> Send</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
